// ── Endpoints ─────────────────────────────────────────────────────────────────

export const API_ENDPOINTS = [
  { api: 'Core API',    domain: 'core.dev.libride.org',    auth: 'API Key (public)',   usedBy: 'Mobile apps on startup — fetches Cognito + AppSync config' },
  { api: 'Ride API',    domain: 'ride.dev.libride.org',    auth: 'Cognito User Pool',  usedBy: 'Rider app, Driver app — ride lifecycle, matching, chat, reviews' },
  { api: 'Driver API',  domain: 'driver.dev.libride.org',  auth: 'Cognito User Pool',  usedBy: 'Driver app — status, earnings, cashout, KYC, trip history' },
  { api: 'Rider API',   domain: 'rider.dev.libride.org',   auth: 'Cognito User Pool',  usedBy: 'Rider app — profile, cash payment, active trip, driver location' },
  { api: 'Payment API', domain: 'payment.dev.libride.org', auth: 'Cognito User Pool',  usedBy: 'Rider app — Paystack card setup, MTN MoMo, Orange Money, wallet' },
  { api: 'KYC API',     domain: 'kyc.dev.libride.org',     auth: 'Cognito User Pool',  usedBy: 'Driver app — document upload, vehicle management, licence submission' },
  { api: 'Admin API',   domain: 'admin.dev.libride.org',   auth: 'Admin Cognito Pool', usedBy: 'This portal — all admin operations' },
];

// ── Subscriptions ─────────────────────────────────────────────────────────────

export const SUBSCRIPTIONS = [
  { name: 'onRiderUpdates(targetId)', api: 'Rider API',  delivers: 'All ride lifecycle events to a specific rider (RIDE_ACCEPTED, DRIVER_ON_THE_WAY, DRIVER_ARRIVED, TRIP_STARTED, TRIP_COMPLETED, TRIP_CANCELLED) plus DRIVER_LOCATION_UPDATE events during active trips' },
  { name: 'onDriverUpdates(targetId)', api: 'Driver API', delivers: 'Ride requests, trip status changes, and forced-offline events to a specific driver' },
  { name: 'onAdminEvent',              api: 'Admin API',  delivers: 'SOS alerts, KYC submissions, and other platform events to admin portal (SuperAdmin, Operations, CustomerService only)' },
];

// ── Per-API operation tables ───────────────────────────────────────────────────

export const RIDE_API_OPS = {
  queries: [
    { op: 'getTrip(id)',                    desc: 'Fetch a single trip by ID' },
    { op: 'getActiveRide(riderId)',          desc: 'Get the current active trip for a rider' },
    { op: 'listTrips(riderId, driverId, status, ...)', desc: 'Paginated trip list, filterable by rider, driver, or status' },
    { op: 'listNearbyDrivers(location, radius)', desc: 'Find available drivers within a radius (km) of a location' },
    { op: 'calculateFare(input)',            desc: 'Estimate fare for a pickup → destination route' },
    { op: 'getGeofence(country, city)',      desc: 'Fetch the service area polygon for a city' },
    { op: 'listAvailableCities',             desc: 'List all cities where the service is active' },
    { op: 'getArchivedRideRequests(riderId, ...)', desc: 'Paginated ride history for a rider (completed + cancelled)' },
    { op: 'getChatMessages(tripId, ...)',    desc: 'Fetch chat messages for an active trip' },
    { op: 'getChatPhotoUploadUrl(tripId)',   desc: 'Get a pre-signed S3 URL to upload a chat photo' },
    { op: 'getReview / getReviewByTrip',    desc: 'Fetch a review by ID or by trip + reviewer role' },
    { op: 'listReviewsForDriver / listReviewsForRider', desc: 'Paginated review lists' },
  ],
  mutations: [
    { op: 'createRideRequest(input)',        desc: 'Rider submits a new ride request — triggers driver matching queue' },
    { op: 'cancelRideRequest(requestId, reason)', desc: 'Cancel a pending or accepted ride request' },
    { op: 'keepLookingRideRequest(input)',   desc: 'Continue searching for a driver after initial timeout' },
    { op: 'updateTripStatus(tripId, status, location)', desc: 'Driver updates trip status (ON_THE_WAY, DRIVER_ARRIVED, etc.)' },
    { op: 'updateDriverLocationToTracker(location, heading, vehicleType)', desc: 'Driver broadcasts location to AWS Location Service when online but no active trip' },
    { op: 'createCallProxy / endCallProxy(tripId)', desc: 'Create or end an anonymised Twilio call proxy between rider and driver' },
    { op: 'sendChatMessage / sendChatPhoto(tripId, ...)', desc: 'Send a text or photo message in the trip chat' },
    { op: 'submitReview(input)',             desc: 'Submit a post-trip rating and review' },
    { op: 'triggerSOSAlert(input)',          desc: 'Rider or driver triggers panic button — notifies admin team immediately' },
  ],
};

export const DRIVER_API_OPS = {
  queries: [
    { op: 'getDriverStatus',                desc: 'Get the current online/offline status of the authenticated driver' },
    { op: 'getActiveTrip(driverId)',         desc: 'Get the driver\'s current active trip' },
    { op: 'getDriverEarnings(period, ...)',  desc: 'Earnings summary for a given period (TODAY, WEEK, MONTH, ALL_TIME)' },
    { op: 'listCashoutRequests',             desc: 'Paginated list of the driver\'s cashout requests' },
    { op: 'listPayoutMethods',               desc: 'List registered MTN MoMo / Orange Money payout methods' },
    { op: 'listTripHistory / getArchivedTripsByDriver', desc: 'Paginated completed trip history' },
  ],
  mutations: [
    { op: 'updateDriverStatus(status, location)', desc: 'Go online or offline — starts/stops location broadcasting' },
    { op: 'respondToRideRequest(requestId, action, location)', desc: 'Accept or decline an incoming ride request' },
    { op: 'performTripAction(tripId, action, location)', desc: 'Advance trip state: ON_THE_WAY → DRIVER_ARRIVED → TRIP_STARTED → COMPLETED' },
    { op: 'confirmCashPayment(input)',       desc: 'Driver confirms cash payment received — generates 4-digit verification code' },
    { op: 'requestCashout(input)',           desc: 'Request a payout to a registered mobile money account' },
    { op: 'addPayoutMethod / removePayoutMethod', desc: 'Manage MTN MoMo / Orange Money payout accounts' },
    { op: 'registerDriverPushToken(input)', desc: 'Register Expo push token for ride request notifications' },
    { op: 'generateProfilePhotoUploadUrl',  desc: 'Get a pre-signed S3 URL to upload a profile photo' },
  ],
};

export const RIDER_API_OPS = {
  queries: [
    { op: 'getRider(id)',                   desc: 'Fetch rider profile' },
    { op: 'getActiveRide(riderId)',          desc: 'Get the rider\'s current active trip' },
  ],
  mutations: [
    { op: 'confirmCashPaymentAsRider(input)', desc: 'Rider enters the 4-digit code to confirm cash payment' },
    { op: 'updateDriverLocation(tripId, riderId, location)', desc: 'Driver streams live location to rider during active trip (called by driver, received via onRiderUpdates subscription)' },
    { op: 'registerRiderPushToken(input)',  desc: 'Register Expo push token for trip update notifications' },
    { op: 'generateProfilePhotoUploadUrl', desc: 'Get a pre-signed S3 URL to upload a profile photo' },
  ],
};

export const PAYMENT_API_OPS = {
  queries: [
    { op: 'getWallet',                      desc: 'Fetch the rider\'s wallet: balance, payment methods, auto-pay setting' },
    { op: 'getPayment(id)',                  desc: 'Fetch a single payment record' },
    { op: 'listPayments(tripId, ...)',       desc: 'Paginated payment history' },
  ],
  mutations: [
    { op: 'initPaystackCardSetup(input)',    desc: 'Initialize a Paystack hosted-checkout session for adding a card' },
    { op: 'verifyPaystackCardSetup(reference)', desc: 'Verify the Paystack transaction and persist the card on the rider wallet' },
    { op: 'addMobileMoneyMethod(input)',     desc: 'Add MTN MoMo or Orange Money — triggers USSD pre-approval prompt' },
    { op: 'setDefaultPaymentMethod(id)',     desc: 'Set the default payment method for new rides' },
    { op: 'removePaymentMethod(id)',         desc: 'Remove a saved payment method' },
    { op: 'retryMtnPreapproval(methodId)',   desc: 'Resend the MTN MoMo USSD pre-approval prompt if it failed or expired' },
  ],
};

export const KYC_API_OPS = {
  queries: [
    { op: 'checkKYCStatus(driverId)',        desc: 'Get the current KYC application status (PENDING, APPROVED, REJECTED)' },
    { op: 'getVehicles',                     desc: 'List all vehicles registered by the driver' },
    { op: 'getMyLicense',                    desc: 'Fetch the driver\'s submitted licence details' },
    { op: 'getDocumentReadUrls(licenseId, vehicleId)', desc: 'Get pre-signed S3 URLs to view uploaded documents' },
  ],
  mutations: [
    { op: 'generateDocumentUploadUrl(input)', desc: 'Get a pre-signed S3 URL to upload a KYC document (licence, registration, insurance)' },
    { op: 'submitKYCApplication(input)',     desc: 'Submit the completed KYC application for admin review' },
    { op: 'addVehicle / updateVehicleDocuments / deleteVehicle', desc: 'Manage registered vehicles' },
    { op: 'setDefaultVehicle / setVehicleType', desc: 'Set the active vehicle and its class (economy, comfort, business)' },
    { op: 'submitLicense / updateLicenseImages', desc: 'Submit or update driver\'s licence images' },
  ],
};

export const CORE_API_OPS = {
  queries: [
    { op: 'getCognitoConfig',               desc: 'Fetch Cognito User Pool ID and Client ID — called on app startup, no auth required' },
    { op: 'getAppsyncConfig',               desc: 'Fetch all AppSync API endpoint URLs — called on app startup, no auth required' },
    { op: 'getCountryCodes',                desc: 'Fetch dial codes and phone number formats for the country picker' },
    { op: 'getAppSettings',                 desc: 'Fetch app version, feature flags, and maintenance mode status' },
    { op: 'getFirehoseCredentials',         desc: 'Fetch limited-access Firehose credentials for mobile error logging — no auth required' },
  ],
};

// ── Auth flow ─────────────────────────────────────────────────────────────────

export const AUTH_FLOW_DONE = [
  'Riders and drivers authenticate via phone number + OTP (Cognito User Pool, phone-based)',
  'Admin portal users authenticate via email + password (separate Cognito User Pool)',
  'Mobile apps fetch Cognito config from Core API on startup — no hardcoded pool IDs in app bundles',
  'Access tokens are stored in device secure storage (not AsyncStorage) on mobile',
  'Token refresh is handled automatically by Amplify; expired sessions trigger re-login',
];

export const AUTH_FLOW_TODO: string[] = [];

// ── Error handling ────────────────────────────────────────────────────────────

export const ERROR_HANDLING_DONE = [
  <>Business logic errors are returned as GraphQL errors with a structured <code>errorType</code> field (e.g. <code>ACTIVE_RIDE_EXISTS</code>, <code>SERVICE_AREA_NOT_COVERED</code>, <code>ACCOUNT_SUSPENDED</code>)</>,
  <>Infrastructure errors (Lambda timeouts, DynamoDB throttles) surface as generic GraphQL errors and are logged to the error pipeline</>,
  <>Frontend apps check <code>error.message</code> for known error codes and show user-friendly messages</>,
];

export const ERROR_HANDLING_TODO: string[] = [];
